/**
 * Blog listing with numbered pagination, matching the search/collection pattern.
 */

import { render } from 'preact'
import { useMemo, useState } from 'preact/hooks'
import { BlogCard } from './BlogCard'
import { Pagination } from '@/components/libs/athos/components/collection/Pagination'

// Read at render time, not module-evaluation time. As a module-scope `const` this was
// captured once at import: if `window.globals.currentBlog` had not been populated yet,
// `data` was permanently `[]` and the grid rendered "No blog posts found" even after the
// global arrived — which also made the retry loop in `initBlogGrid` unable to ever
// succeed, however long it ran.
const getArticles = () => window.globals?.currentBlog?.articles || []

function useBlogPagination(totalPages) {
  const [currentPage, setCurrentPage] = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const pageParam = parseInt(params.get('page'), 10)
    if (!pageParam || pageParam < 1) return 1
    return Math.min(pageParam, Math.max(totalPages, 1))
  })

  const setPage = (page) => {
    setCurrentPage(page)

    const params = new URLSearchParams(window.location.search)
    if (page > 1) params.set('page', page.toString())
    else params.delete('page')

    const query = params.size > 0 ? `?${params.toString()}` : ''
    window.history.replaceState({}, '', `${window.location.pathname}${query}`)
  }

  return [currentPage, setPage]
}

function BlogGrid() {
  const data = getArticles()
  const getDomBlogs = document.getElementById('list-blogs')
  const resultsPerPage = parseInt(getDomBlogs?.getAttribute('per-page') || 9)
  const totalPages = Math.max(Math.ceil(data.length / resultsPerPage), 1)

  const [currentPage, setCurrentPage] = useBlogPagination(totalPages)

  const pageBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage
    return data.slice(startIndex, startIndex + resultsPerPage)
  }, [currentPage, resultsPerPage])

  if (!data || data.length === 0) {
    return <div><p className="paragraph-m">No blog posts found.</p></div>
  }

  return (
    <div className="grow flex flex-col px-0 pt-1.5 md:pt-2.5">
        <div
        className="grid grid-cols-1 md:grid-cols-6 gap-x-gutter gap-y-1.5 lg:gap-y-2 w-full"
        >
        {
            pageBlogs?.map((blog, index) => {
              // Row 2 (the 4th and 5th posts) renders as 2 featured cards
              // instead of 3 normal ones, each spanning half the row.
              const isFeatured = index === 3 || index === 4
              const colSpanClass = isFeatured ? 'md:col-span-3' : 'md:col-span-2'
              return <BlogCard blog={blog} key={blog.id} dataIndex={index.toString()} className={colSpanClass} featured={isFeatured} />
            })
        }
        </div>
        {totalPages > 1 && (
        <div className="flex justify-center px-margin py-3.5">
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
        </div>
        )}
    </div>
  )
}

function RenderContent() {
  return <BlogGrid />
}

// Bounded retry. This used to re-arm every 100ms with no cap, so on any blog page where
// the global never arrived it ran at 10Hz for the life of the page. `shop-the-look.ts`
// bounds its own retry chain for exactly this reason.
const INIT_RETRY_INTERVAL_MS = 100
const INIT_MAX_ATTEMPTS = 50 // ~5s

function initBlogGrid(attempt = 0) {
  const container = document.getElementById('list-blogs')
  if (container && window.globals?.currentBlog?.articles) {
    render(<RenderContent />, container)
    return
  }

  if (attempt >= INIT_MAX_ATTEMPTS) {
    // Render anyway so the container shows the empty state rather than staying blank.
    if (container) render(<RenderContent />, container)
    return
  }

  setTimeout(() => initBlogGrid(attempt + 1), INIT_RETRY_INTERVAL_MS)
}

if (document.readyState === 'loading') {
  // Wrapped, not passed directly: as a listener it would receive the Event as `attempt`,
  // and `[object Event] + 1` would make the retry counter never reach its cap.
  document.addEventListener('DOMContentLoaded', () => initBlogGrid())
} else {
  initBlogGrid()
}
